/**
 * PortalView — NIC-district-portal shell for a location article.
 *
 * Mounts on `/l/<slug>?view=portal` (see LocationArticle.jsx toggle).
 * Consumes `/api/portal/<slug>` which returns 20 top-level sections
 * with country-aware labels. Left rail = collapsible section tree;
 * center = content of the currently-selected subsection.
 *
 * Content resolution per `data.type`:
 *   • ai        → AiTopicContent w/ useAiTopic + RelatedLinksPanel
 *   • poi       → count card + link to /places/<slug>/<category>
 *   • children  → grid of child locations (cities/towns/villages)
 *   • links     → curated useful-links list
 *   • feature   → CTA card linking to the existing site page
 *   • news      → embed LocalNewsFeed
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Loader2, ChevronRight, ChevronDown, ArrowLeft, MapPin, ExternalLink,
  BookOpen, Landmark, GitBranch, Building, ClipboardList, Camera, Briefcase,
  Users, AlertTriangle, Plane, Utensils, Home, Newspaper, Siren, Sparkles,
  Vote, Download, MessageSquare, Globe,
} from "lucide-react";
import AiTopicContent from "@/components/AiTopicContent";
import useAiTopic from "@/hooks/useAiTopic";
import RelatedLinksPanel from "@/components/RelatedLinksPanel";
import LocalNewsFeed from "@/components/LocalNewsFeed";
import PlaceStamp from "@/components/PlaceStamp";
import PlaceKindTag from "@/components/PlaceKindTag";
import useSite from "@/hooks/useSite";

const API = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  BookOpen, Landmark, GitBranch, Building, ClipboardList, MapPin, Camera,
  Briefcase, Users, AlertTriangle, Plane, Utensils, Home, Newspaper, Siren,
  Sparkles, Vote, Download, MessageSquare, Globe,
};

export default function PortalView({ locationSlug, name, onExit }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const [openTop, setOpenTop] = useState(() => new Set(["about"]));
  const { slug: siteSlug } = useSite();

  const activeSectionSlug = params.get("s") || "about";
  const activeSubSlug     = params.get("sub") || "";

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/api/portal/${locationSlug}`, { params: { site: siteSlug } })
      .then((r) => alive && setPayload(r.data))
      .catch(() => alive && setPayload(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [locationSlug, siteSlug]);

  const currentSub = useMemo(() => {
    if (!payload) return null;
    const sec = payload.sections.find((s) => s.slug === activeSectionSlug);
    if (!sec) return null;
    if (!activeSubSlug) return sec.subsections[0] || null;
    return sec.subsections.find((s) => s.slug === activeSubSlug) || sec.subsections[0] || null;
  }, [payload, activeSectionSlug, activeSubSlug]);

  const toggleTop = (slug) => {
    setOpenTop((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  };

  const selectSub = (secSlug, subSlug) => {
    const next = new URLSearchParams(params);
    next.set("view", "portal");
    next.set("s", secSlug);
    next.set("sub", subSlug);
    setParams(next, { replace: false });
    // Ensure the enclosing top-level is expanded.
    setOpenTop((prev) => new Set(prev).add(secSlug));
  };

  if (loading) {
    return <div className="max-w-6xl mx-auto py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60" /></div>;
  }
  if (!payload) {
    return <div className="max-w-6xl mx-auto py-16 text-center opacity-70">Couldn't load portal for this location.</div>;
  }

  return (
    <article className="max-w-7xl mx-auto px-4 lg:px-6 py-8" data-testid={`portal-view-${payload.location.slug}`}>
      {/* Toggle back to article view */}
      <div className="mb-4 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs font-mono uppercase tracking-widest opacity-70">
          <Link to="/" className="hover:underline">Home</Link>
          <span className="mx-1 opacity-40">·</span>
          <Link to={`/l/${payload.location.slug}`} className="hover:underline">{payload.location.name}</Link>
          <span className="mx-1 opacity-40">·</span>
          <span>Portal View</span>
        </div>
        <button
          onClick={onExit}
          className="text-xs font-mono uppercase tracking-widest rounded-full px-3 py-1.5 border hover:brightness-95 transition inline-flex items-center gap-1"
          style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
          data-testid="portal-view-exit"
        >
          <ArrowLeft className="w-3 h-3" /> Article view
        </button>
      </div>

      <div className="grid lg:grid-cols-[300px_1fr] gap-6">
        {/* LEFT — collapsible section tree */}
        <aside className="lg:sticky lg:top-6 self-start max-h-[85vh] overflow-y-auto rounded-2xl border p-3" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }} data-testid="portal-nav">
          {(() => {
            const segs = (payload.location.slug || "").split("/").filter(Boolean);
            const last = (segs[segs.length - 1] || "").toLowerCase();
            const kind = (payload.location.kind || (/-county$|-parish$|-borough$/.test(last)
              ? "county"
              : segs.length === 1 ? "country"
              : segs.length === 2 ? "state"
              : "city")).toLowerCase();
            return (
              <div className="flex items-center gap-3 mb-3 pb-3 border-b" style={{ borderColor: "var(--wofy-border)" }}>
                <PlaceStamp
                  kind={kind}
                  slug={payload.location.slug}
                  size={48}
                  className="shadow-sm ring-1 ring-white/60"
                  title={payload.location.name}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg leading-tight truncate">{payload.location.name}</div>
                  <div className="text-xs opacity-70 truncate">
                    {[payload.location.state, payload.location.country].filter(Boolean).join(" · ")}
                  </div>
                  <div className="mt-1">
                    <PlaceKindTag kind={kind} slug={payload.location.slug} country={payload.location.country} />
                  </div>
                </div>
              </div>
            );
          })()}
          <nav className="space-y-0.5">
            {payload.sections.map((sec) => {
              const Icon = ICONS[sec.icon] || MapPin;
              const open = openTop.has(sec.slug);
              const isActive = sec.slug === activeSectionSlug;
              return (
                <div key={sec.slug} data-testid={`portal-sec-${sec.slug}`}>
                  <button
                    onClick={() => toggleTop(sec.slug)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-black/[0.04] transition"
                    style={isActive ? { background: "#EFF6FF", color: "#1D4ED8", fontWeight: 600 } : {}}
                    data-testid={`portal-toggle-${sec.slug}`}
                  >
                    {open ? <ChevronDown className="w-3.5 h-3.5 opacity-60" /> : <ChevronRight className="w-3.5 h-3.5 opacity-60" />}
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="flex-1 text-left truncate">{sec.label}</span>
                    <span className="text-[10px] font-mono opacity-50">{sec.subsections.length}</span>
                  </button>
                  {open && (
                    <ul className="ml-6 border-l pl-2 py-0.5 space-y-0.5" style={{ borderColor: "var(--wofy-border)" }}>
                      {sec.subsections.map((sub) => {
                        const active = sec.slug === activeSectionSlug && (activeSubSlug === sub.slug || (!activeSubSlug && sub === sec.subsections[0]));
                        return (
                          <li key={sub.slug}>
                            <button
                              onClick={() => selectSub(sec.slug, sub.slug)}
                              className="w-full text-left text-[13px] px-2 py-1 rounded-md hover:bg-black/[0.04] transition truncate"
                              style={active ? { background: "#DBEAFE", color: "#1D4ED8", fontWeight: 600 } : {}}
                              data-testid={`portal-sub-${sec.slug}-${sub.slug}`}
                            >
                              {sub.label}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>

        {/* CENTER — currently selected subsection */}
        <div className="min-w-0" data-testid="portal-content">
          {currentSub ? (
            <SubsectionContent sub={currentSub} location={payload.location} />
          ) : (
            <div className="opacity-70">Pick a topic from the left rail.</div>
          )}
        </div>
      </div>
    </article>
  );
}

function SubsectionContent({ sub, location }) {
  const d = sub.data || {};
  const title = `${sub.label} — ${location.name}`;

  if (d.type === "ai") {
    return <AiSubsection entityKey={d.entity_key} title={title} sub={sub} location={location} />;
  }

  if (d.type === "poi") {
    return (
      <>
        <SectionHeader title={sub.label} location={location} />
        <div className="rounded-2xl border-2 p-5 mb-6" style={{ borderColor: "#DBEAFE", background: "#F8FAFF" }} data-testid="portal-poi">
          <div className="text-sm opacity-80 mb-2">
            <strong>{d.count}</strong> {sub.label.toLowerCase()} indexed for {location.name}.
          </div>
          {d.preview?.length > 0 && (
            <ul className="grid sm:grid-cols-2 gap-2 mb-3">
              {d.preview.map((p) => (
                <li key={p.slug} className="text-sm truncate">
                  <Link to={`/places/detail/${p.slug}`} className="hover:underline">
                    <MapPin className="w-3.5 h-3.5 inline mr-1 opacity-60" />{p.name}
                    {p.verified && <span className="ml-1 text-[10px] text-green-700">✓</span>}
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {d.href && (
            <Link to={d.href} className="text-sm underline decoration-2 underline-offset-4">
              See all {sub.label.toLowerCase()} →
            </Link>
          )}
        </div>
        <RelatedLinksPanel query={`${sub.label} ${location.name}`} />
      </>
    );
  }

  if (d.type === "children") {
    return (
      <>
        <SectionHeader title={sub.label} location={location} />
        <div className="rounded-2xl border p-5 mb-6" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }} data-testid="portal-children">
          <div className="text-sm opacity-80 mb-3">
            <strong>{d.count}</strong> {d.kind}{d.count === 1 ? "" : "s"} inside {location.name}.
          </div>
          {d.preview?.length > 0 ? (
            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {d.preview.map((c) => (
                <li key={c.slug}>
                  <Link to={`/l/${c.slug}`} className="text-sm truncate hover:underline block">
                    <MapPin className="w-3.5 h-3.5 inline mr-1 opacity-60" />{c.name}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm opacity-70">None indexed yet.</p>
          )}
        </div>
        <RelatedLinksPanel query={`${sub.label} in ${location.name}`} />
      </>
    );
  }

  if (d.type === "links") {
    return (
      <>
        <SectionHeader title={sub.label} location={location} />
        <div className="rounded-2xl border p-5 mb-6" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }} data-testid="portal-links">
          {d.preview?.length > 0 ? (
            <ul className="space-y-2">
              {d.preview.map((l, i) => (
                <li key={i}>
                  <a href={l.url} target="_blank" rel="noreferrer" className="text-sm hover:underline inline-flex items-start gap-1.5">
                    <ExternalLink className="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-60" />{l.caption}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm opacity-70">No links yet.</p>
          )}
        </div>
      </>
    );
  }

  if (d.type === "feature" && d.target) {
    return (
      <>
        <SectionHeader title={sub.label} location={location} />
        <Link to={d.target.href} className="block rounded-2xl border-2 p-5 hover:shadow-md transition" style={{ borderColor: "#DBEAFE", background: "linear-gradient(135deg, #EFF6FF 0%, #F0FDFA 100%)" }} data-testid="portal-feature">
          <div className="text-xs font-mono uppercase tracking-widest opacity-60 mb-1">Open module</div>
          <div className="font-display text-2xl">{d.target.label}</div>
          <div className="text-sm opacity-70 mt-1">Everything Social Pulse has for {sub.label.toLowerCase()} — filtered for {location.name} where available.</div>
        </Link>
      </>
    );
  }

  if (d.type === "news") {
    return (
      <>
        <SectionHeader title={sub.label} location={location} />
        <LocalNewsFeed slug={location.slug} />
        <RelatedLinksPanel query={`${location.name} news`} />
      </>
    );
  }

  return <div className="opacity-70">No content for this section yet.</div>;
}

function AiSubsection({ entityKey, title, sub, location }) {
  const topic = useAiTopic({
    source:    "portal",
    entityKey,
    title,
    context:   `Encyclopedia-style entry for the "${sub.label}" section of a ${location.kind} portal about ${location.name}. Focus specifically on the ${location.name} ${sub.label} topic — what it is, key facilities/offices/officials/programs, how residents interact with it, useful resources, and interesting facts.`,
    deepLink:  null,
    enabled:   true,
  });
  return (
    <>
      <SectionHeader title={sub.label} location={location} />
      <AiTopicContent doc={topic.doc} status={topic.status} retry={topic.retry} title={title} />
      <RelatedLinksPanel query={`${sub.label} ${location.name}`} />
    </>
  );
}

function SectionHeader({ title, location }) {
  return (
    <div className="mb-4">
      <div className="text-xs font-mono uppercase tracking-widest opacity-60 mb-1">{location.name}</div>
      <h1 className="font-display text-3xl leading-tight" data-testid="portal-section-title">{title}</h1>
    </div>
  );
}
